import { Component, OnInit, ViewChild } from '@angular/core';
import {
  FormsModule,
  NgForm,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { ReAuthPopupService } from './services/reauth-popup.service';
import { CVANotificationService } from 'app/services/cva-notification/cva-notification.service';
import { NotificationClassEnum } from 'app/models/notification-class.enum';
import { CustomLoginService } from '@privatelib/ngx18-authz-dev';
import { LoginService } from 'app/services/login/login.service';
import { ROUTE_CONSTANTS } from 'app/util/route.constants';
import { CVA_CONSTANTS } from 'app/util/constants';
import { MatDialogRef } from '@angular/material/dialog';
import { finalize } from 'rxjs';

@Component({
  selector: 'cva-standalone-reauth-popup',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './reauth-popup.component.html',
  styleUrl: './reauth-popup.component.scss'
})
export class ReauthPopupComponent implements OnInit {
  @ViewChild('reAuthNgForm') reAuthNgForm: NgForm;

  reAuthForm: UntypedFormGroup;

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly reAuthPopupService: ReAuthPopupService,
    private readonly cvaNotificationService: CVANotificationService,
    private readonly customLoginService: CustomLoginService,
    private readonly loginService: LoginService,
    private readonly router: Router,
    private dialogRef: MatDialogRef<ReauthPopupComponent>
  ) { }

  ngOnInit(): void {
    const email = this.reAuthPopupService.getUserEmail();
    if (!email) {
      this.finalLogout();
      console.error("Unable to fetch user email")
    }
    this.reAuthForm = this.formBuilder.group({
      email: [email, [Validators.required, Validators.email]],
      password: ['', Validators.required]
    });
  }

  reAuthenticate(): void {
    if (this.reAuthForm.invalid) {
      return;
    }

    this.reAuthForm.disable();
    // this.showAlert = false;

    this.customLoginService.login(this.reAuthForm.value)
      .pipe(
        finalize(() => {
          // Re-enable the form
          this.reAuthForm.enable();
          this.reAuthForm.markAsUntouched();
        })
      )
      .subscribe({
        next: (res: { body: { redirectUrl: string } }) => {
          console.log("Re-login successful!");

          this.reAuthForm.reset();

          const redirectUrl = res.body?.redirectUrl;
          if (redirectUrl) {
            this.reAuthPopupService.saveTokenOnReLogin(redirectUrl);
            this.dialogRef.close(true);
          } else {
            this.finalLogout();
          }
        }, error: (error) => {
          console.log("Re-login failed");
          // Reset the form
          this.reAuthForm.patchValue({ password: '' });

          if (!error?.resetLink) {
            this.cvaNotificationService.showNotification(
              error?.message || CVA_CONSTANTS.MESSAGE.UNKNOWN_LOGIN_ERR,
              NotificationClassEnum.ERROR,
              8000
            );
          }

          if (error?.resetLink) {
            this.loginService.redirectToResetPassword(error.resetLink);
            this.dialogRef.close(false);
          } else if (error.isPermanentLockout) {
            this.finalLogout();
          }
        }
      });
  }

  finalLogout() {
    this.router.navigate([ROUTE_CONSTANTS.LOGIN]);
    this.dialogRef.close(false);
  }
}
